import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import UrgentAttentionBar from '../components/dashboard/UrgentAttentionBar';
import GettingStartedLane from '../components/dashboard/GettingStartedLane';
import ComplianceHealthScore from '../components/dashboard/ComplianceHealthScore';
import UpcomingDeadlines from '../components/dashboard/UpcomingDeadlines';
import RecentActivityFeed from '../components/dashboard/RecentActivityFeed';
import QuickActions from '../components/dashboard/QuickActions';
import MetricsStrip from '../components/dashboard/MetricsStrip';

function SkeletonCard({ className = '' }: { className?: string }) {
  return (
    <div className={`animate-pulse bg-white rounded-lg shadow-sm border border-gray-100 p-6 ${className}`}>
      <div className="h-4 w-32 bg-gray-200 rounded mb-4" />
      <div className="h-8 w-20 bg-gray-200 rounded mb-3" />
      <div className="space-y-2">
        <div className="h-3 w-full bg-gray-200 rounded" />
        <div className="h-3 w-3/4 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Urgent attention skeleton */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse rounded-lg bg-white border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 bg-gray-200 rounded-lg" />
              <div>
                <div className="h-7 w-10 bg-gray-200 rounded mb-1" />
                <div className="h-3 w-20 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Two-column row skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SkeletonCard />
        <SkeletonCard />
      </div>

      {/* Two-column row skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SkeletonCard />
        <SkeletonCard />
      </div>

      {/* Metrics strip skeleton */}
      <div className="animate-pulse bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="h-5 w-5 bg-gray-200 rounded mb-1" />
              <div className="h-6 w-12 bg-gray-200 rounded mb-1" />
              <div className="h-3 w-20 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  // Pin the dashboard to the user's currently-active team. Without this,
  // the backend would fall back to picking the user's earliest team
  // membership, which can differ from the team displayed elsewhere in
  // the app for users who belong to multiple teams (e.g., owners on the
  // demo team + a real team). QA #3 #5 — two users on the same team saw
  // different stats.
  const { teamId } = useTeamId();
  const { data: healthData, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboardHealth', teamId],
    queryFn: () => dashboardApi.getHealth(teamId),
    refetchInterval: 60000,
    enabled: !!teamId,
  });

  const data = healthData?.data;

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">Your compliance command center.</p>
      </div>

      {isError ? (
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8 text-center">
          <p className="text-red-600 font-medium mb-2">Failed to load dashboard data</p>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm">
            Retry
          </button>
        </div>
      ) : isLoading || !data ? (
        <DashboardSkeleton />
      ) : (
        <div className="space-y-6">
          {/* New / zero-data accounts: show the setup path first */}
          {data.healthScore === null && <GettingStartedLane />}

          {/* Row 1: Urgent Attention */}
          <UrgentAttentionBar data={data.urgentAttention} healthScore={data.healthScore} />

          {/* Row 2: Health Score + Deadlines */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <ComplianceHealthScore
                score={data.healthScore}
                breakdown={data.healthBreakdown}
              />
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <UpcomingDeadlines deadlines={data.upcomingDeadlines} />
            </div>
          </div>

          {/* Row 3: Activity Feed + Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <RecentActivityFeed activity={data.recentActivity} />
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <QuickActions />
            </div>
          </div>

          {/* Row 4: Metrics Strip */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <MetricsStrip metrics={data.metrics} />
          </div>
        </div>
      )}
    </div>
  );
}
